import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface OpcionMenu {
  clave: string;
  etiqueta: string;
  icono?: ReactNode;
  atajo?: string;
  peligro?: boolean;
  desactivada?: boolean;
  /** Motivo por el que está desactivada; se muestra como tooltip. */
  motivo?: string;
  separadorAntes?: boolean;
  accion(): void;
}

interface Props {
  x: number;
  y: number;
  opciones: OpcionMenu[];
  alCerrar(): void;
}

export function MenuContextual({ x, y, opciones, alCerrar }: Props) {
  const caja = useRef<HTMLDivElement>(null);
  const [posicion, setPosicion] = useState({ x, y });

  // Si el menú se abre cerca del borde, se corre hacia adentro: un menú
  // cortado por la ventana es un menú inservible.
  useLayoutEffect(() => {
    const elemento = caja.current;
    if (!elemento) return;
    const { width, height } = elemento.getBoundingClientRect();
    setPosicion({
      x: Math.min(x, window.innerWidth - width - 8),
      y: Math.min(y, window.innerHeight - height - 8),
    });
  }, [x, y]);

  useEffect(() => {
    const cerrar = () => alCerrar();
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && alCerrar();
    document.addEventListener('mousedown', cerrar);
    document.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', cerrar);
      document.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [alCerrar]);

  return (
    <div
      className="menu"
      ref={caja}
      style={{ left: posicion.x, top: posicion.y }}
      onMouseDown={(e) => e.stopPropagation()}
      role="menu"
    >
      {opciones.map((o) => (
        <div key={o.clave}>
          {o.separadorAntes && <div className="menu__sep" />}
          <button
            className={`menu__item${o.peligro ? ' menu__item--peligro' : ''}`}
            disabled={o.desactivada}
            title={o.desactivada ? o.motivo : undefined}
            onClick={() => {
              o.accion();
              alCerrar();
            }}
            role="menuitem"
          >
            {o.icono}
            <span>{o.etiqueta}</span>
            {o.atajo && <span className="atajo">{o.atajo}</span>}
          </button>
        </div>
      ))}
    </div>
  );
}
